import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Calendar, Video, MapPin, Clock, X, MessageSquare, ShieldCheck, ShieldAlert, Ban, ChevronDown, ClipboardList, CheckCircle2 } from "lucide-react";
import { Skeleton } from "@/components/Skeleton";
import useModalA11y from "@/lib/useModalA11y";

// Remote rehabilitation review has been retired — omitted from the picker.
const SERVICES = [
  "Initial sport therapy assessment",
  "Follow-up sport therapy",
  "Sports massage",
  "Strength & conditioning",
  "Return-to-sport testing",
];

const LDN = { timeZone: "Europe/London" };
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { ...LDN, day: "numeric", month: "short", year: "numeric" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-GB", { ...LDN, hour: "2-digit", minute: "2-digit" });
const STATUS_META = {
  pending:      { label: "Pending",       bg: "rgba(245,158,11,.15)",  fg: "#F59E0B", Icon: Clock },
  confirmed:    { label: "Confirmed",     bg: "rgba(34,197,94,.15)",   fg: "#22C55E", Icon: ShieldCheck },
  declined:     { label: "Declined",      bg: "rgba(239,68,68,.15)",   fg: "#EF4444", Icon: ShieldAlert },
  cancelled:    { label: "Cancelled",     bg: "rgba(239,68,68,.10)",   fg: "#EF4444", Icon: Ban },
  rescheduled:  { label: "Rescheduled",   bg: "rgba(59,130,246,.15)",  fg: "#3B82F6", Icon: Calendar },
  completed:    { label: "Completed",     bg: "rgba(34,197,94,.12)",   fg: "#22C55E", Icon: CheckCircle2 },
};

function todayISO() { return new Date().toISOString().slice(0, 10); }

// Patient-SAFE booking timeline from the existing status_history — plain,
// non-clinical labels only. Never exposes clinician names, internal notes,
// decline/reopen reasons, roles or technical event names.
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString("en-GB", { ...LDN, day: "numeric", month: "short" }); } catch { return ""; } };
function buildPatientTimeline(a) {
  if (!a) return { events: [], reopened: false };
  const events = [];
  if (a.created_at) events.push({ label: "Requested", at: a.created_at });
  let reopened = false;
  for (const h of a.status_history || []) {
    if (h.to === "declined") events.push({ label: "Declined", at: h.at });
    else if (h.to === "cancelled") events.push({ label: "Cancelled", at: h.at });
    else if (h.to === "pending" && ["declined", "cancelled"].includes(h.from)) { reopened = true; events.push({ label: "Reopened", at: h.at }); }
    else if (h.to === "confirmed") events.push({ label: "Confirmed", at: h.at });
    else if (h.to === "rescheduled") events.push({ label: "Rescheduled", at: h.at });
    else if (h.to === "completed") events.push({ label: "Completed", at: h.at });
  }
  return { events, reopened };
}

export default function PatientAppointments() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const preselectService = params.get("service") || null;
  const preselectClinician = params.get("clinician") || null;
  const preselectCase = params.get("case") || null;

  const [appts, setAppts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [detail, setDetail] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const detailRef = useModalA11y({ open: !!detail, onClose: () => setDetail(null) });
  // Expand Booking history by default only after a meaningful reversal (reopen).
  useEffect(() => { if (detail) setHistoryOpen(buildPatientTimeline(detail).reopened); }, [detail]);
  // Merge any pre-selected service (from a "Book" link on /prices) into the
  // dropdown list so the form's initial value is guaranteed to render.
  const serviceOptions = useMemo(() => {
    if (preselectService && !SERVICES.includes(preselectService)) return [preselectService, ...SERVICES];
    return SERVICES;
  }, [preselectService]);
  const [f, setF] = useState({
    clinician_id: "",
    service: preselectService || "Initial sport therapy assessment",
    mode: "in_person",
    date: todayISO(),
    slot: "",
    booking_reason: "",
  });
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [apptsLoading, setApptsLoading] = useState(true);
  // iter99 · UI collapsibility for the booking card + past-appointments list.
  // Booking form is OPEN by default so patients can book in fewer clicks.
  const [bookingOpen, setBookingOpen] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const refresh = async () => {
    // Real active bookable clinicians (real user ids) — same source the
    // registration picker uses. Availability + Google FreeBusy are keyed off
    // these user ids.
    const [a, c] = await Promise.all([api.get("/appointments/mine"), api.get("/public/clinicians")]);
    setAppts(a.data);
    api.get("/session-reviews/mine").then((r) => setReviews(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    const list = c.data || [];
    setClinicians(list);
    if (list.length && !f.clinician_id) {
      // Default to the patient's assigned clinician, then any ?clinician= param,
      // then the first in the list. All remain selectable.
      const assignedId = user?.assigned_clinician_id || user?.clinician_id || null;
      const pick =
        (assignedId && list.find((x) => x.id === assignedId)) ||
        (preselectClinician && list.find((x) => x.id === preselectClinician)) ||
        list[0];
      setF((s) => ({ ...s, clinician_id: pick.id }));
    }
    setApptsLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const loadSlots = async () => {
    if (!f.clinician_id || !f.date) return;
    setSlotsLoading(true);
    setSlots([]);
    setF((s) => ({ ...s, slot: "" }));
    try {
      const r = await api.get(`/appointments/availability`, {
        params: { clinician_id: f.clinician_id, date: f.date, duration_min: 60 },
      });
      setSlots(r.data?.slots || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load slots");
    } finally { setSlotsLoading(false); }
  };
  useEffect(() => { loadSlots(); /* eslint-disable-next-line */ }, [f.clinician_id, f.date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.slot) return toast.error("Pick a time slot");
    if (isFirstBooking && !f.booking_reason.trim())
      return toast.error("Please tell your clinician what you'd like help with.");
    try {
      await api.post("/appointments", {
        patient_id: user.id,
        clinician_id: f.clinician_id,
        service: f.service,
        mode: f.mode,
        scheduled_at: f.slot,
        duration_min: 60,
        case_id: preselectCase || null,
        booking_reason: f.booking_reason.trim() || null,
      });
      toast.success("Appointment booked");
      setF((s) => ({ ...s, slot: "", booking_reason: "" }));
      setBookingOpen(false); // collapse to "Book a new session" after a booking
      refresh();
      loadSlots();
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); }
  };

  // First meaningful booking → reason required. Cancelled/declined don't count.
  const isFirstBooking = useMemo(
    () => !appts.some((a) => !["cancelled", "declined"].includes(a.status)),
    [appts],
  );

  const availableCount = useMemo(() => slots.filter((s) => s.available).length, [slots]);

  return (
    <DashLayout>
      <div className="mb-8">
        <div className="fl-label text-[#f47a20]">{t("pages.appointments.eyebrow")}</div>
        <h1 className="fl-heading text-4xl mt-2">{t("pages.appointments.h1")}</h1>
      </div>

      <div className="grid md:grid-cols-5 gap-6">
        <div className="md:col-span-2">
          {/* Compact square booking card — expands into the full form on tap */}
          {!bookingOpen ? (
            <button
              type="button"
              onClick={() => setBookingOpen(true)}
              className="fl-card fl-card-elevated aspect-square w-full p-6 flex flex-col justify-between hover:border-[#f47a20]/40 transition-colors text-left"
              data-testid="booking-toggle"
            >
              <div className="flex items-center gap-2 text-[#f47a20]">
                <Calendar size={20} />
                <div className="fl-label">Booking</div>
              </div>
              <div>
                <div className="fl-heading text-3xl leading-tight">Book a new session</div>
                <div className="text-xs text-zinc-400 mt-2">
                  Tap to open the picker — choose a clinician, service, mode and time.
                </div>
              </div>
              <div className="text-[#f47a20] text-xs uppercase tracking-widest font-semibold flex items-center gap-1">
                Open picker <span aria-hidden>→</span>
              </div>
            </button>
          ) : (
            <div className="fl-card fl-card-elevated p-6" data-testid="booking-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="fl-heading text-xl">New booking</div>
                <button
                  type="button"
                  onClick={() => setBookingOpen(false)}
                  className="text-xs text-zinc-400 hover:text-white"
                  data-testid="booking-close"
                >
                  ✕ Close
                </button>
              </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="fl-label mb-2">Clinician</div>
              <select className="fl-input" value={f.clinician_id}
                      onChange={(e) => setF({ ...f, clinician_id: e.target.value })}
                      data-testid="appt-clinician" required>
                <option value="" disabled>Choose a clinician</option>
                {clinicians.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <div className="fl-label mb-2">Service</div>
              <select className="fl-input" value={f.service}
                      onChange={(e) => setF({ ...f, service: e.target.value })}
                      data-testid="appt-service">
                {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div className="fl-label mb-2">{isFirstBooking ? "Reason for booking *" : "Reason for booking (optional)"}</div>
              <textarea
                className="fl-input min-h-[70px]" rows={3}
                value={f.booking_reason}
                onChange={(e) => setF({ ...f, booking_reason: e.target.value })}
                placeholder={isFirstBooking ? "Briefly tell your clinician what you would like help with." : "Add anything new you would like your clinician to know."}
                data-testid="appt-booking-reason"
                required={isFirstBooking}
              />
              <div className="text-[11px] mt-1" style={{ color: "var(--fl-muted-2)" }}>
                {isFirstBooking
                  ? "Briefly tell your clinician what you would like help with."
                  : "Add anything new you would like your clinician to know."}
              </div>
            </div>
            <div>
              <div className="fl-label mb-2">Mode</div>
              <div className="flex gap-2">
                {["in_person", "remote"].map((m) => (
                  <button type="button" key={m} onClick={() => setF({ ...f, mode: m })}
                          className={`flex-1 px-4 py-2 rounded-md border text-sm ${f.mode === m ? "border-[#f47a20] text-[#f47a20]" : "border-white/10 text-zinc-400"}`}
                          data-testid={`appt-mode-${m}`}>
                    {m === "in_person" ? "In person" : "Remote"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="fl-label mb-2">Date</div>
              <input type="date" min={todayISO()} className="fl-input" value={f.date}
                     onChange={(e) => setF({ ...f, date: e.target.value })}
                     data-testid="appt-date" required />
            </div>
            <div>
              <div className="fl-label mb-2">Available times</div>
              {slotsLoading ? (
                <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>Loading…</div>
              ) : slots.length === 0 ? (
                <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>Pick a clinician and date to see slots.</div>
              ) : availableCount === 0 ? (
                <div className="text-xs" style={{ color: "var(--fl-muted-2)" }} data-testid="appt-no-slots">
                  No slots left on this day — try another date.
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-1.5" data-testid="appt-slots">
                  {slots.map((s) => {
                    const active = f.slot === s.iso;
                    return (
                      <button
                        key={s.iso}
                        type="button"
                        disabled={!s.available}
                        onClick={() => setF((v) => ({ ...v, slot: s.iso }))}
                        data-testid={`appt-slot-${s.time.replace(":", "")}`}
                        className="px-2 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        style={{
                          background: active
                            ? "var(--fl-orange)"
                            : (s.available ? "color-mix(in oklab, var(--fl-orange) 10%, transparent)" : "transparent"),
                          color: active ? "#fff" : (s.available ? "var(--fl-orange)" : "var(--fl-muted-2)"),
                          border: `1px solid ${s.available ? "color-mix(in oklab, var(--fl-orange) 30%, transparent)" : "var(--fl-border)"}`,
                        }}
                      >
                        <Clock size={11} className="inline -mt-0.5 mr-1" />
                        {s.time}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button className="fl-btn w-full justify-center" data-testid="appt-submit" disabled={!f.slot}>
              <Calendar size={14} /> Book
            </button>
          </form>
            </div>
          )}
        </div>

        <div className="md:col-span-3">
          <div className="space-y-6">
            {apptsLoading && appts.length === 0 && (
              <div className="space-y-3" data-testid="appts-loading">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="fl-card fl-card-elevated p-5 flex items-center gap-4">
                    <Skeleton className="h-6 w-6 rounded" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-56 rounded-md" />
                      <Skeleton className="h-3 w-40 mt-2 rounded" />
                    </div>
                    <div>
                      <Skeleton className="h-5 w-16 rounded-md" />
                      <Skeleton className="h-3 w-12 mt-2 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(() => {
              const now = Date.now();
              const endMs = (a) => new Date(a.scheduled_at).getTime() + (a.duration_min || 60) * 60000;
              const isPast = (a) => endMs(a) < now; // past once the END time (London-correct, absolute) has elapsed
              const byTimeAsc = (x, y) => new Date(x.scheduled_at) - new Date(y.scheduled_at);
              const byTimeDesc = (x, y) => new Date(y.scheduled_at) - new Date(x.scheduled_at);
              const pending = appts.filter((a) => !isPast(a) && a.status === "pending").sort(byTimeAsc);
              const upcoming = appts.filter((a) => !isPast(a) && ["confirmed", "rescheduled"].includes(a.status || "confirmed")).sort(byTimeAsc);
              const history = appts.filter((a) => isPast(a) || ["declined", "cancelled", "completed"].includes(a.status)).sort(byTimeDesc);
              const renderAppt = (a, historical = false) => {
                const st = STATUS_META[a.status || "confirmed"] || STATUS_META.confirmed;
                const StIcon = st.Icon;
                // Patient-facing label: pending reads as "Awaiting confirmation".
                const label = a.status === "pending" ? "Awaiting confirmation" : st.label;
                return (
                  <button
                    key={a.id}
                    onClick={() => setDetail(a)}
                    className={`w-full text-left fl-card fl-card-elevated p-5 flex items-center gap-4 transition-colors hover:border-[#f47a20]/40 ${historical ? "opacity-60" : ""}`}
                    data-testid={`appt-${a.id}`}
                  >
                    {a.mode === "remote" ? <Video className="text-[#f47a20]" size={20} /> : <MapPin className="text-[#f47a20]" size={20} />}
                    <div className="flex-1 min-w-0">
                      <div className="fl-heading text-lg truncate">{a.service}</div>
                      <div className="text-xs text-zinc-500 truncate">with {a.clinician_name || "Team"} · {a.duration_min} min</div>
                      <div className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
                           style={{ background: st.bg, color: st.fg, border: `1px solid ${st.fg}55` }}
                           data-testid={`appt-status-${a.id}`}>
                        <StIcon size={10} /> {label}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="fl-heading text-lg text-[#f47a20]">{fmtDate(a.scheduled_at).split(" ").slice(0, 2).join(" ")}</div>
                      <div className="text-xs text-zinc-500">{fmtTime(a.scheduled_at)}</div>
                    </div>
                  </button>
                );
              };
              return (
                <>
                  {/* Pending requests — reserved time awaiting clinician confirmation */}
                  {pending.length > 0 && (
                    <div data-testid="pending-section">
                      <div className="fl-heading text-xl mb-3">Pending requests</div>
                      <div className="text-xs mb-3" style={{ color: "var(--fl-muted-2)" }}>Your request was submitted — awaiting clinician confirmation.</div>
                      <div className="space-y-3">{pending.map((a) => renderAppt(a))}</div>
                    </div>
                  )}

                  {/* Upcoming — confirmed/active future appointments only */}
                  <div data-testid="upcoming-section">
                    <div className="fl-heading text-xl mb-3">Upcoming appointments</div>
                    <div className="space-y-3">
                      {upcoming.length === 0 && !apptsLoading && (
                        <div className="fl-card fl-card-elevated p-6 text-zinc-500 text-sm" data-testid="upcoming-empty">
                          No upcoming appointments — book a new session on the left.
                        </div>
                      )}
                      {upcoming.map((a) => renderAppt(a))}
                    </div>
                  </div>

                  {/* History — past, declined, cancelled, completed (read-only) */}
                  {history.length > 0 && (
                    <div data-testid="history-section">
                      <button
                        type="button"
                        onClick={() => setShowHistory((v) => !v)}
                        className="fl-btn-ghost text-xs w-full justify-center"
                        data-testid="appt-history-toggle"
                      >
                        {showHistory ? "Hide history" : `Show history (${history.length})`}
                      </button>
                      {showHistory && (
                        <div className="space-y-3 mt-3" data-testid="appt-history-list">
                          {history.map((a) => renderAppt(a, true))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {detail && (() => {
        const st = STATUS_META[detail.status || "confirmed"] || STATUS_META.confirmed;
        const StIcon = st.Icon;
        const timeline = buildPatientTimeline(detail);
        const whenMs = new Date(detail.scheduled_at).getTime();
        const hoursUntil = (whenMs - Date.now()) / 3600000;
        const canRequestChange = hoursUntil > 24;
        const canOnlyMessage = hoursUntil > 0 && hoursUntil <= 24;
        const reviewForDetail = reviews.find((r) => r.appointment_id === detail.id);
        const requestChange = () => {
          const first = (detail.clinician_name || "there").split(" ")[0];
          // Prefill only the greeting — the patient writes the rest themselves.
          const body = `Hi ${first}, `;
          nav(`/app/messages?to=${encodeURIComponent(detail.clinician_id || "")}&prefill=${encodeURIComponent(body)}`);
        };
        return (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-auto" onClick={() => setDetail(null)}>
            <div
              ref={detailRef}
              role="dialog"
              aria-modal="true"
              className="fl-card fl-card-elevated p-6 w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
              data-testid="appt-detail-modal"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="fl-label text-[#f47a20]">Appointment</div>
                  <div className="fl-heading text-2xl mt-1">{detail.service}</div>
                  <div className="text-xs text-zinc-500 mt-1">with {detail.clinician_name || "your clinician"}</div>
                  {detail.case_title && <div className="mt-1 text-[11px] text-[#f8a766]" data-testid="appt-detail-case">{detail.case_title}</div>}
                </div>
                <button onClick={() => setDetail(null)} aria-label="Close" data-testid="appt-detail-close"><X size={16} className="text-zinc-400"/></button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="fl-card p-3">
                  <div className="fl-label mb-1">When (London time)</div>
                  <div className="text-sm">{fmtDate(detail.scheduled_at)}</div>
                  <div className="text-sm text-[#f47a20]">{fmtTime(detail.scheduled_at)} · {detail.duration_min} min</div>
                </div>
                <div className="fl-card p-3">
                  <div className="fl-label mb-1">Mode</div>
                  <div className="text-sm capitalize">{(detail.mode || "in_person").replace("_", " ")}</div>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full"
                     style={{ background: st.bg, color: st.fg, border: `1px solid ${st.fg}55` }}
                     data-testid="appt-detail-status">
                  <StIcon size={11} /> {st.label}
                </div>
                {timeline.reopened && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full"
                        style={{ background: "rgba(59,130,246,.15)", color: "#3B82F6", border: "1px solid rgba(59,130,246,.4)" }}
                        data-testid="appt-detail-reopened-tag">
                    Reopened
                  </span>
                )}
                {detail.previous_scheduled_at && (
                  <div className="text-[10px] text-zinc-500">was {fmtDate(detail.previous_scheduled_at)} {fmtTime(detail.previous_scheduled_at)}</div>
                )}
              </div>

              {timeline.events.length > 1 && (
                <div className="mb-4" data-testid="appt-booking-history">
                  <button type="button" onClick={() => setHistoryOpen((v) => !v)}
                          className="w-full flex items-center justify-between text-xs text-zinc-400 hover:text-white py-1"
                          data-testid="appt-booking-history-toggle">
                    <span className="fl-label">Booking history</span>
                    <ChevronDown size={14} className={`transition-transform ${historyOpen ? "rotate-180" : ""}`} />
                  </button>
                  {historyOpen && (
                    <ol className="mt-2 border-l border-white/10 pl-4 space-y-2" data-testid="appt-booking-history-list">
                      {timeline.events.map((e, i) => (
                        <li key={i} className="relative text-xs">
                          <span className="absolute -left-[21px] top-1 w-1.5 h-1.5 rounded-full" style={{ background: i === timeline.events.length - 1 ? "var(--fl-orange)" : "#52525b" }} />
                          <span className="text-zinc-200">{e.label}</span>
                          <span className="text-zinc-500"> — {fmtDay(e.at)}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}

              {detail.status_note && (
                <div className="fl-card p-3 mb-4 text-xs" style={{ color: "var(--fl-text-2)" }}>
                  <div className="fl-label mb-1">Note from your clinician</div>
                  {detail.status_note}
                </div>
              )}

              {reviewForDetail && (
                <div className="fl-card p-4 mb-4" data-testid="patient-session-review">
                  <div className="flex items-center gap-2 mb-2">
                    <ClipboardList size={14} className="text-[#f47a20]" />
                    <div className="fl-label">Session review</div>
                    {reviewForDetail.case_title && <span className="text-[11px] text-[#f8a766]">· {reviewForDetail.case_title}</span>}
                  </div>
                  <div className="text-[11px] text-zinc-500 mb-2">{fmtDay(reviewForDetail.published_at)}</div>
                  <div className="space-y-2 text-sm">
                    {reviewForDetail.progress_summary && <div><div className="fl-label">Progress since last review</div><div className="text-zinc-300">{reviewForDetail.progress_summary}</div></div>}
                    {reviewForDetail.current_focus && <div><div className="fl-label">Current focus</div><div className="text-zinc-300">{reviewForDetail.current_focus}</div></div>}
                    {reviewForDetail.next_step && <div><div className="fl-label">Next step</div><div className="text-zinc-300">{reviewForDetail.next_step}</div></div>}
                    {reviewForDetail.patient_message && <div><div className="fl-label">Message from your clinician</div><div className="text-zinc-300">{reviewForDetail.patient_message}</div></div>}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2">
                {canRequestChange && (
                  <button onClick={requestChange} className="fl-btn w-full justify-center" data-testid="appt-detail-request-change">
                    <MessageSquare size={14} /> Request a change (message clinician)
                  </button>
                )}
                {canOnlyMessage && (
                  <div className="fl-card p-3 text-xs" style={{ color: "var(--fl-muted)", border: "1px solid rgba(239,68,68,.35)" }}>
                    Changes cannot be requested less than 24 hours before the session. Please message your clinician if urgent.
                    <button onClick={requestChange} className="fl-btn-ghost text-xs mt-2" data-testid="appt-detail-message-clinician">
                      <MessageSquare size={12} /> Message clinician
                    </button>
                  </div>
                )}
                <button onClick={() => setDetail(null)} className="fl-btn-ghost w-full justify-center">Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </DashLayout>
  );
}
